import Head from "next/head";
import Link from "next/link";
import { AdminLayout } from "components/admin/admin-layout";
import { gql } from "@apollo/client";
import { useGetVouchersQuery } from "lib/graphql-generated";
import { Button } from "components/buttons/button";
import { useRouter } from "next/router";
import currency from "currency.js";
import { useInput } from "lib/hooks/use-input";
import { AdminSearchBar } from "components/admin/admin-search-bar";
import { parseParam } from "lib/utils";
import querystring from "querystring";
import { inputClassNames, labelClassNames } from "components/forms/input";
import { GenerateVoucherReportForm } from "components/admin/vouchers/GenerateVoucherReportForm";
const GET_VOUCHERS = gql`
  query GetVouchers(
    $limit: Int
    $offset: Int
    $conditions: vouchers_bool_exp!
  ) {
    vouchers_aggregate(
      where: {
        _and: [
          $conditions
          {
            _or: [
              { order: { status: { _neq: draft } } }
              { order_id: { _is_null: true } }
            ]
          }
        ]
      }
    ) {
      aggregate {
        count
      }
    }

    vouchers(
      limit: $limit
      offset: $offset
      where: {
        _and: [
          $conditions
          {
            _or: [
              { order: { status: { _neq: draft } } }
              { order_id: { _is_null: true } }
            ]
          }
        ]
      }
      order_by: { created_at: desc }
    ) {
      id
      recipient
      initial_amount
      remaining_amount
      created_at
    }
  }
`;

export const Vouchers = () => {
  /**
   * Query Strings
   */
  const router = useRouter();
  const pQuery = parseInt(router.query["p"] as string);
  const paginate = Boolean(pQuery) ? pQuery : 0;
  const limit = 25;
  const searchQuery = parseParam(router.query["search"]) || "";
  const offset = Boolean(paginate) ? paginate * limit : 0;
  const search = useInput<string>(searchQuery);

  const getPathWithQuery = ({
    p,
    status,
    search,
    order,
  }: {
    p?: number;
    status?: string;
    search?: string;
    order?: string;
  } = {}) => {
    const queryString = querystring.encode({
      p: p ?? paginate ?? 0,
      search: search ?? searchQuery ?? "",
    });
    return `${router.pathname}?${queryString}`;
  };

  const { data, loading, error, refetch } = useGetVouchersQuery({
    variables: {
      limit: limit,
      offset: offset,
      conditions: {
        _or: [
          { recipient: { _ilike: `%${searchQuery}%` } },
          { id: { _ilike: `%${searchQuery}%` } },
        ],
      },
    },
    fetchPolicy: "cache-and-network",
  });

  const handleSearch = (e) => {
    router.replace(`/admin/vouchers?p=0&s=${search.value}`);
    search.init(search.value);
  };

  return (
    <>
      <AdminLayout
        page='Vouchers'
        searchBar={
          <AdminSearchBar
            {...search.bind}
            onSearch={() =>
              router.replace(getPathWithQuery({ search: search.value, p: 0 }))
            }
            placeholder='Search by recipient'
          />
        }>
        <article className='space-y-4'>
          <header className='flex flex-col xl:flex-row flex-wrap w-full justify-center space-y-4 xl:space-y-0 xl:space-x-4'>
            <h1 className='header text-4xl ml-auto mr-auto xl:ml-0 xl:mr-auto'>
              Vouchers / Gift cards
            </h1>
            <Button
              buttonType='secondary'
              buttonSize='lg'
              href='/admin/vouchers/new'
              className='my-auto ml-auto mr-auto'>
              <svg
                className='w-4 h-4 mr-2 -ml-1 mb-1'
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                />
              </svg>
              <span>New Voucher</span>
            </Button>
          </header>

          <table className='min-w-full divide-y divide-gray-200 shadow overflow-hidden border-b border-gray-200 sm:rounded-lg'>
            <thead className=' bg-gray-100 text-left text-xs leading-4 font-medium text-gray-700 uppercase tracking-wider'>
              <tr>
                <th className='px-6 py-3'>Code</th>
                <th className='px-6 py-3'>Recipient</th>
                <th className='px-6 py-3 hidden xl:table-cell'>
                  Remaining Amount
                </th>
                <th className='px-6 py-3 hidden xl:table-cell'>Date Created</th>
              </tr>
            </thead>
            <tbody className='bg-white divide-y divide-gray-200 text-sm text-gray-800'>
              {loading ? (
                <tr className='hover:bg-gray-300'>
                  <td
                    className='px-6 py-4 whitespace-no-wrap leading-5 text-center'
                    colSpan={4}>
                    Loading Data...
                  </td>
                </tr>
              ) : data.vouchers.length !== 0 ? (
                data?.vouchers.map((voucher) => (
                  <tr
                    key={voucher.id}
                    className='hover:bg-gray-300 cursor-pointer'
                    onClick={() =>
                      router.push(`/admin/vouchers/${voucher.id}`)
                    }>
                    <td className='px-6 py-4 whitespace-no-wrap leading-5'>
                      {voucher.id}
                    </td>
                    <td className='px-6 py-4 whitespace-no-wrap leading-5'>
                      {voucher.recipient}
                    </td>
                    <td className='px-6 py-4 whitespace-no-wrap leading-5'>
                      {
                        currency(voucher.remaining_amount, { fromCents: true })
                          .value
                      }
                    </td>
                    <td className='px-6 py-4 whitespace-no-wrap leading-5 hidden xl:table-cell'>
                      {new Date(voucher.created_at).toLocaleDateString("en-GB")}
                    </td>
                  </tr>
                ))
              ) : (
                <tr className='hover:bg-gray-300'>
                  <td
                    className='px-6 py-4 whitespace-no-wrap leading-5 text-center'
                    colSpan={4}>
                    Could not find vouchers...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </article>
        {data && (
          <nav
            className='py-3 flex items-center justify-between border-t border-gray-200 '
            aria-label='Pagination'>
            <div className='hidden sm:block'>
              <p className='text-sm text-gray-700'>
                Showing{" "}
                <span className='font-medium'>
                  {offset}
                  {` `}
                </span>
                to{" "}
                <span className='font-medium'>
                  {offset + limit}
                  {` `}
                </span>
                of{" "}
                <span className='font-medium'>
                  {data?.vouchers_aggregate.aggregate.count}
                  {` `}
                </span>
                results
              </p>
            </div>
            <div className='flex-1 flex justify-between sm:justify-end'>
              {paginate !== 0 && (
                <Link href={getPathWithQuery({ p: paginate - 1 })}>
                  <a className='relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50'>
                    Previous
                  </a>
                </Link>
              )}
              {paginate !==
                Math.floor(data.vouchers_aggregate.aggregate.count / 25) && (
                <Link href={getPathWithQuery({ p: paginate + 1 })}>
                  <a className='ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50'>
                    Next
                  </a>
                </Link>
              )}
            </div>
          </nav>
        )}
        <GenerateVoucherReportForm />
      </AdminLayout>
    </>
  );
};

export default Vouchers;
